import { useRef, useState } from 'react';
import { getDayOfWeek, now } from '~/lib/datetime/mod.js';
import { useEffectOnMounted } from '~/lib/hooks/mod.js';
import { capitalize, tkns } from '~/lib/str/mod.js';
import { ChevronIcon } from '~/ui/Images/mod.js';

import { findNowEntry, getRenderedScheduleEntries } from './nowEntry.js';

const NOW_RECHECK_INTERVAL_MS = 60 * 1000;

/**
 * How far into the viewport the header + sticky date heading reach from the top, and the date nav
 * from the bottom
 */
const measureObstructions = () => {
	const scrollPaddingTop =
		parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
	const dateHeadingHeight =
		document.querySelector('.date-heading')?.getBoundingClientRect().height ?? 0;
	const footerTop = document.querySelector('#app > footer')?.getBoundingClientRect().top;

	return {
		top: scrollPaddingTop + dateHeadingHeight,
		bottom: footerTop === undefined ? 0 : window.innerHeight - footerTop,
	};
};

export const DateJumpBar = ({ dates, id, a11yLabel }) => {
	/** @type {import('react').MutableRefObject<HTMLElement> | undefined} */
	const navRef = useRef();
	/** @type {import('react').MutableRefObject<boolean>} Set while a clicked link's smooth-scroll
	 * jump is still animating, so scroll-driven updates don't fight with the class set immediately
	 * on click. */
	const isJumpingRef = useRef(false);
	/** @type {import('react').MutableRefObject<(() => void) | null>} */
	const jumpCleanupRef = useRef(null);

	/** @type {import('react').MutableRefObject<HTMLElement | undefined>} The Now button's target */
	const nowTargetRef = useRef();
	const [isNowVisible, setIsNowVisible] = useState(false);
	// Which way the current session is off-screen, for the Now button's chevron
	const [nowDirection, setNowDirection] = useState(/** @type {'up' | 'down'} */ ('down'));

	const handleDateLinkClick = dayId => () => {
		const targetHref = '#'.concat(dayId);
		navRef.current?.querySelectorAll('a[href^="#day"]').forEach(link => {
			link.classList.toggle('current', link.getAttribute('href') === targetHref);
		});

		// A rapid second click shouldn't leave the first jump's cleanup dangling.
		jumpCleanupRef.current?.();
		isJumpingRef.current = true;

		const finishJump = () => {
			isJumpingRef.current = false;
			window.removeEventListener('scrollend', finishJump);
			clearTimeout(fallbackTimeoutId);
			jumpCleanupRef.current = null;
		};
		// `scrollend` covers browsers that support it; the timeout is a fallback for those that
		// don't (Safari < 17.4), and a safety net in case it never fires for any other reason.
		window.addEventListener('scrollend', finishJump, { once: true });
		const fallbackTimeoutId = setTimeout(finishJump, 1000);
		jumpCleanupRef.current = finishJump;
	};

	const handleNowClick = () => {
		const target = nowTargetRef.current;
		if (!target) {
			return;
		}

		// Land the entry just below both the fixed header and its day's sticky heading.
		const scrollPaddingTop =
			parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
		const dateHeadingHeight =
			target
				.closest('.schedule-date-group')
				?.querySelector('.date-heading')
				?.getBoundingClientRect().height ?? 0;
		window.scrollTo({
			top:
				target.getBoundingClientRect().top + window.scrollY - scrollPaddingTop - dateHeadingHeight,
		});

		// The button hides once the entry scrolls into view, which would drop keyboard focus back to
		// the start of the document. Move focus to the entry instead, so the next Tab continues from
		// there. `tabindex` is only needed to make it focusable for this, so it's removed again once
		// focus moves elsewhere in the page.
		if (!target.hasAttribute('tabindex')) {
			const handleBlur = () => {
				// `blur` also fires when the whole window loses focus (switching apps/tabs, or to
				// devtools), but the entry stays the document's active element then. Removing
				// `tabindex` at that point would make it unfocusable and drop focus to <body>.
				if (document.activeElement === target) {
					return;
				}
				target.removeAttribute('tabindex');
				target.removeEventListener('blur', handleBlur);
			};
			target.setAttribute('tabindex', '-1');
			target.addEventListener('blur', handleBlur);
		}
		target.focus({ preventScroll: true });
	};

	useEffectOnMounted(() => {
		if (!navRef.current) {
			return undefined;
		}

		const dayIds = dates.map((_, i) => 'day'.concat(String(i + 1)));
		// The `#day#` elements double as scroll-jump anchors (see SchedulePage) and, being placed
		// immediately before each sticky `.date-heading`, as sentinels: a heading starts sticking to
		// the top of the screen at the exact moment its sentinel's natural position crosses the header.
		const dayEls = dayIds.map(dayId => document.getElementById(dayId)).filter(Boolean);
		const links = dayIds.map(dayId => navRef.current.querySelector(`a[href="#${dayId}"]`));

		// `html { scroll-padding-top: var(--header-height); }` is what the browser actually uses to
		// decide where a jump-link's sentinel comes to rest, so read that value directly rather than
		// independently measuring the header element -- two elements that should align in theory can
		// still get pixel-snapped to very slightly different fractional values during layout.
		const getScrollBoundary = () => {
			const parsed = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop);
			// A small tolerance absorbs any residual rounding drift between a sentinel's own
			// pixel-snapped rect and this parsed value.
			return (Number.isNaN(parsed) ? 0 : parsed) + 1;
		};

		const updateCurrentLink = () => {
			// A click's own handler already applied `.current` synchronously; don't let this
			// observer/resize-driven recompute fight with it while that jump is still animating.
			if (isJumpingRef.current) {
				return;
			}

			const boundary = getScrollBoundary();

			// Default to the first date, so it reads as current for as long as the page is scrolled
			// above its sentinel (including on initial load).
			let currentIndex = 0;
			dayEls.forEach((dayEl, i) => {
				if (dayEl.getBoundingClientRect().top <= boundary) {
					currentIndex = i;
				}
			});

			// The last date's section could be too short to ever scroll its own sentinel past the
			// header (there's nothing below it left to scroll into), so fall back to treating it as
			// current once the page has scrolled as far as it can go.
			const isScrolledToBottom =
				window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
			if (isScrolledToBottom) {
				currentIndex = dayEls.length - 1;
			}

			links.forEach((link, i) => {
				link?.classList.toggle('current', i === currentIndex);
			});
		};

		// A clicked link's own handler now applies `.current` immediately and suppresses this
		// observer for the duration of that jump (see `isJumpingRef`), so the observer no longer
		// needs to resolve the click-jump's landing spot itself -- it's only responsible for organic
		// scrolling, where a sentinel reliably crosses (rather than lands exactly on) the boundary.
		const observer = new IntersectionObserver(updateCurrentLink, {
			rootMargin: `-${getScrollBoundary()}px 0px 0px 0px`,
			threshold: 0,
		});
		dayEls.forEach(dayEl => observer.observe(dayEl));
		window.addEventListener('resize', updateCurrentLink);

		updateCurrentLink();

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updateCurrentLink);
		};
	});

	useEffectOnMounted(() => {
		/** @type {IntersectionObserver | undefined} */
		let nowObserver;

		const trackNowTarget = () => {
			nowObserver?.disconnect();

			const target = findNowEntry(getRenderedScheduleEntries(), now())?.el;
			nowTargetRef.current = target;
			if (!target) {
				setIsNowVisible(false);
				return;
			}

			// The root is shrunk to the band of the viewport not covered by the header/date heading
			// and the date nav, so "intersecting" means at least part of the entry is actually visible.
			const obstructions = measureObstructions();
			nowObserver = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) {
						setIsNowVisible(false);
						return;
					}
					// Set alongside the reveal, so the chevron is already in place as it slides in.
					setNowDirection(entry.boundingClientRect.bottom <= obstructions.top ? 'up' : 'down');
					setIsNowVisible(true);
				},
				{
					rootMargin: `-${obstructions.top}px 0px -${obstructions.bottom}px 0px`,
					threshold: 0,
				}
			);
			nowObserver.observe(target);
		};

		trackNowTarget();
		// Re-pick the target as time moves on, and re-measure when the layout changes size.
		const intervalId = setInterval(trackNowTarget, NOW_RECHECK_INTERVAL_MS);
		window.addEventListener('resize', trackNowTarget);

		return () => {
			nowObserver?.disconnect();
			clearInterval(intervalId);
			window.removeEventListener('resize', trackNowTarget);
		};
	});

	return (
		<nav className="date-jump-bar" id={id} aria-label={a11yLabel} ref={navRef}>
			<ol>
				{dates.map((date, i) => {
					const dayId = 'day'.concat(String(i + 1));
					return (
						<li key={dayId}>
							<a href={'#'.concat(dayId)} onClick={handleDateLinkClick(dayId)}>
								<time dateTime={date.toISOString().split('T')[0]}>
									{capitalize(getDayOfWeek(date))} {date.getDate()}
								</time>
							</a>
						</li>
					);
				})}
			</ol>

			<button
				type="button"
				className={tkns('date-jump-bar-now', `points-${nowDirection}`, {
					'is-visible': isNowVisible,
				})}
				onClick={handleNowClick}
				aria-label="Jump to the current session"
			>
				{/* strokeWidth 4 in the 16-unit viewBox at 8px = a 2px stroke */}
				<ChevronIcon size="8" strokeWidth={4} className="date-jump-bar-now-chevron" />
				Now
			</button>
		</nav>
	);
};
